import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SearchBar from 'ui/SearchBar/SearchBar';
import {
    OfficialGrade,
    OfficialGradeTree,
} from 'routes/HumanResourcesModule/Settings/HrGrade/helpers/type';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { IconButton, styled } from '@mui/material';
import OfficialGradeTreeItem from 'routes/HumanResourcesModule/Settings/HrGrade/components/OfficialGradeTreeItem';
import OfficialGradeItem, {
    StyTreeItem,
} from 'routes/HumanResourcesModule/Settings/HrGrade/components/OfficialGradeItem';
import { filterOfficialGradesTree } from 'routes/HumanResourcesModule/Settings/HrGrade/components/helpers/treeHelpers';
import { TreeView } from '@mui/x-tree-view';
const StySearchBarWrapper = styled('div', {
    name: 'StySearchBarWrapper',
})({
    position: 'sticky',
    zIndex: 1,
    top: 0,
    left: 0,
    width: '100%',
    background: 'white',
    padding: '1.5rem 0 0 0',
    transform: 'translateY(-1.5rem)',
});

const selectGradeId = (
    setSelectedGradeId: (value: number | null) => void,
    newGradeId: number,
    isChecked: boolean,
) => {
    const newId = isChecked ? newGradeId : null;
    setSelectedGradeId(newId);
};

const getOfficialGradeItem =
    (
        selectedGradeId: number | null,
        setSelectedGradeId: (value: number | null) => void,
    ) =>
    (element: OfficialGrade): React.ReactElement => {
        const checked = selectedGradeId === element.id;

        const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            selectGradeId(setSelectedGradeId, element.id, event.target.checked);
        };
        return (
            <OfficialGradeItem
                key={element.id}
                officialGrade={element}
                checked={checked ?? false}
                onChange={onChange}
            />
        );
    };

const getOfficialGradeTreeItem =
    (
        selectedGradeId: number | null,
        setSelectedGradeId: (value: number | null) => void,
    ) =>
    (element: OfficialGradeTree): React.ReactElement => {
        const cadreEmploiSet = Array.from(
            new Set(element.children.map((el) => el.cadreEmploi)),
        );
        const cadreEmploiElements = cadreEmploiSet.map(
            (cadreEmploi: string) => (
                <StyTreeItem
                    nodeId={`cadreEmploi-${cadreEmploi}`}
                    label={cadreEmploi}
                    key={cadreEmploi}
                >
                    {element.children.length > 0
                        ? element.children
                              .filter((el) => el.cadreEmploi === cadreEmploi)
                              .map(
                                  getOfficialGradeItem(
                                      selectedGradeId,
                                      setSelectedGradeId,
                                  ),
                              )
                        : undefined}
                </StyTreeItem>
            ),
        );
        return (
            <OfficialGradeTreeItem
                key={element.filiere}
                filiere={element.filiere}
            >
                {cadreEmploiElements}
            </OfficialGradeTreeItem>
        );
    };

type Props = {
    selectedGradeId: number | null;
    setSelectedGradeId: (gradeId: number | null) => void;
    officialGradesTree: OfficialGradeTree[];
};

function OfficialGradeSelector({
    selectedGradeId,
    setSelectedGradeId,
    officialGradesTree,
}: Props) {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedRows, setExpandedRows] = useState<string[]>([]);
    const [filteredOfficialGradesTree, setFilteredOfficialGradesTree] =
        useState(officialGradesTree);

    useEffect(() => {
        setFilteredOfficialGradesTree(
            filterOfficialGradesTree(
                officialGradesTree,
                searchQuery,
                setExpandedRows,
            ),
        );
    }, [searchQuery, JSON.stringify(officialGradesTree)]);

    const handleSearchQueryChange = (newSearchQuery: string) => {
        setSearchQuery(newSearchQuery.toLowerCase());
    };

    const handleToggle = (__: React.SyntheticEvent, nodeIds: string[]) => {
        setExpandedRows(nodeIds);
    };

    return (
        <>
            <StySearchBarWrapper>
                <SearchBar
                    handleChange={handleSearchQueryChange}
                    placeholder={t('mappings.searchPlaceholder')}
                    width="100%"
                    autoFocus
                />
            </StySearchBarWrapper>
            <TreeView
                defaultCollapseIcon={
                    <IconButton aria-label="collapse">
                        <ExpandMoreIcon />
                    </IconButton>
                }
                defaultExpandIcon={
                    <IconButton aria-label="expand">
                        <ChevronRightIcon />
                    </IconButton>
                }
                expanded={expandedRows}
                onNodeToggle={handleToggle}
                disableSelection
            >
                {filteredOfficialGradesTree.map(
                    getOfficialGradeTreeItem(
                        selectedGradeId,
                        setSelectedGradeId,
                    ),
                )}
            </TreeView>
        </>
    );
}

export default OfficialGradeSelector;
